/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo, useRef } from 'react';
import {
	Box,
	FormLabel,
	FormControl,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
	Tooltip,
	Stack,
} from '@mui/material';
import { useRefinementList } from 'react-instantsearch';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { minimalLabelTheme } from '@/app/theme';
import { getComparator, stableSort } from '@/components/table/functions';
import { useDispatch, useSelector } from 'react-redux';
import { setFilters } from '@/features/table/instantSearchSlice';
import { usePipelineContext } from '@/context/pipelineContext';
import { useGetTaskCategoriesQuery } from '@/features/engagements/engagementsApi';
import { useInstantSearchOwnerFilters } from '@/hooks/useInstantSearchOwnerFilters';
import {
	RELATIONSHIP_CONTACT_STATUS_LABELS,
	RELATIONSHIP_CONTACT_TYPE_LABELS,
} from '@/utils/relationshipContactOptions';
import { OverflowChips } from '@/components/OverflowChips';
import { useChipFit } from '@/hooks/useChipFit';

export const RefinementListSelect = ({
	title,
	disabled,
	multiple,
	pipeline,
	tab = 'active',
	minimal = true,
	slug: slugProp,
	disableTooltip = false,
	...props
}) => {
	const dispatch = useDispatch();

	const { filters, slug } = useInstantSearchOwnerFilters(slugProp);

	const { data: categoriesData } = useGetTaskCategoriesQuery(undefined, {
		skip: props.attribute !== 'category',
	});
	const categories = categoriesData ?? [];

	const currentFilters = filters[props.attribute] ?? [];

	const { stages, inactive, completed } = usePipelineContext(
		props.attribute === 'stage' ? undefined : pipeline,
	);

	const isStageIdInactive = (stageId) =>
		inactive.some((stage) => (typeof stage === 'object' ? stage.id : stage) === stageId);

	const isStageIdCompleted = (stageId) =>
		completed.some((stage) => (typeof stage === 'object' ? stage.id : stage) === stageId);

	const { items, refine } = useRefinementList(props);

	const prospectPipelineStages = useMemo(() => {
		if (props.attribute !== 'stage') return [];

		return (
			stages?.filter(
				(stage) => stage.pipelineId === process.env.REACT_APP_PIPELINE_PROSPECTS,
			) ?? []
		);
	}, [props.attribute, stages]);

	const refactoredItems = useMemo(() => {
		if (props.attribute === 'relationshipType') {
			return items.map((item) => ({
				...item,
				label: RELATIONSHIP_CONTACT_TYPE_LABELS[item.value] ?? item.label,
			}));
		}

		if (props.attribute === 'relationshipStatus') {
			return items.map((item) => ({
				...item,
				label: RELATIONSHIP_CONTACT_STATUS_LABELS[item.value] ?? item.label,
			}));
		}

		if (props.attribute === 'category') {
			return items.map((item) => {
				const category = categories.find((c) => c.value === item.value);
				return { ...item, ...(category && { label: category.label }) };
			});
		}

		if (props.attribute === 'stage' && prospectPipelineStages.length > 0) {
			const openStages = prospectPipelineStages.filter((stage) => !stage.closed);

			const pipelineItems = openStages
				.map((stage) => {
					const stageId = stage.id ?? stage.stageId;
					const facetItem = items.find((item) => item.value === stageId);
					return { stage, facetItem };
				})
				// Only show stages that have prospects (same behaviour as debtors status filter)
				.filter(({ facetItem }) => (facetItem?.count ?? 0) > 0)
				.map(({ stage, facetItem }) => ({
					label: stage?.name ?? stage?.label ?? stage.id,
					value: facetItem.value,
					order: stage.displayOrder ?? stage.order ?? 0,
					isRefined: facetItem.isRefined ?? false,
					count: facetItem.count,
				}));

			return stableSort(pipelineItems, getComparator('asc', 'order'));
		}

		const itemsWithStages = items.map((item) => {
			const stage = stages.find((s) => s.id == item.value);
			return {
				...item,
				...(stage && { label: stage.label, order: stage.displayOrder }),
			};
		});
		return stages.length > 0
			? stableSort(itemsWithStages, getComparator('asc', 'order'))
			: itemsWithStages;
	}, [items, stages, categories, props.attribute, prospectPipelineStages, slug]);

	const tabFilteredItems = useMemo(() => {
		if (props.attribute !== 'dealStage') {
			return refactoredItems;
		}

		if (tab === 'all') {
			return refactoredItems;
		}

		if (tab === 'invoiced') {
			return refactoredItems.filter((item) => isStageIdCompleted(item.value));
		}

		return refactoredItems.filter((item) => !isStageIdInactive(item.value));
	}, [refactoredItems, props.attribute, tab, inactive, completed]);

	const all = useMemo(
		() => ({
			label: 'All',
			value: 'All',
			isRefined: tabFilteredItems.every((item) => item.isRefined),
		}),
		[tabFilteredItems],
	);

	const none = useMemo(
		() => ({
			label: 'Clear Filters',
			value: 'None',
			isRefined: tabFilteredItems.every((item) => !item.isRefined),
		}),
		[tabFilteredItems],
	);

	const shouldSplit = props.attribute === 'yearRoot';

	const allItems = useMemo(() => {
		const baseItems = shouldSplit
			? [...tabFilteredItems]
			: multiple
				? [all.isRefined ? none : all, ...tabFilteredItems]
				: [none, ...tabFilteredItems];

		return baseItems;
	}, [tabFilteredItems, shouldSplit, multiple, all, none]);

	const selectedItems = useMemo(
		() => tabFilteredItems.filter((i) => i.isRefined),
		[tabFilteredItems],
	);

	const containerRef = useRef();
	const limitTags = useChipFit(containerRef, selectedItems.length);

	useEffect(() => {
		const hasFilterValue = (filterValue) =>
			currentFilters.some((currentValue) => currentValue == filterValue);

		if (currentFilters.length === 0) {
			items
				.filter((item) => item.isRefined)
				.forEach((item) => {
					refine(item.value);
				});
			return;
		}

		currentFilters.forEach((filterValue) => {
			const item = items.find((candidate) => candidate.value == filterValue);
			if (item && !item.isRefined) {
				refine(item.value);
			}
		});

		items
			.filter((item) => item.isRefined && !hasFilterValue(item.value))
			.forEach((item) => {
				refine(item.value);
			});
	}, [currentFilters, items, refine]);

	const handleChange = (event) => {
		let values = event.target.value;
		// console.log('NEW SELECTION', values);
		// const refined = items.filter((i) => i.isRefined).map((v) => v.value);
		if (multiple) {
			values = values.map((v) => (typeof v === 'string' ? v : v.value));
			if (values.includes('All')) {
				// Refine all items
				tabFilteredItems.forEach((item) => {
					if (!item.isRefined) refine(item.value);
				});
				dispatch(
					setFilters({
						slug,
						type: props.attribute,
						values: tabFilteredItems.map((i) => i.value),
					}),
				);
				return;
			} else if (values.includes('None')) {
				// refined.forEach((item) => refine(item));
				dispatch(setFilters({ slug, type: props.attribute, values: [] }));
			} else {
				var multi = values.reduce((o, n) => {
					n in o ? (o[n] += 1) : (o[n] = 1);
					return o;
				}, {});

				var res = Object.keys(multi).filter((k) => multi[k] == 1);
				dispatch(setFilters({ slug, type: props.attribute, values: res }));
			}
		} else {
			const value = typeof values === 'string' ? values : values.value;
			if (value == 'None') {
				// refined.forEach((item) => refine(item));
				dispatch(setFilters({ slug, type: props.attribute, values: [] }));
			} else {
				dispatch(
					setFilters({
						slug,
						type: props.attribute,
						values: currentFilters.includes(value) ? [] : [value],
					}),
				);
			}
		}
	};

	return (
		<Stack sx={{ ...(props?.sx ?? {}) }}>
			<FormLabel>{title}</FormLabel>

			<Tooltip
				disableFocusListener
				title={
					disableTooltip
						? ''
						: refactoredItems
								.filter((i) => i.isRefined)
								.map((i) => i.label)
								.join(', ')
				}
				disableHoverListener={disableTooltip}
				placement='top'
			>
				<Select
					ref={containerRef}
					multiple={multiple}
					fullWidth={true}
					disabled={disabled}
					sx={{
						maxHeight: 35,
						alignItems: 'center',
					}}
					value={multiple ? selectedItems : (selectedItems[0]?.value ?? '')}
					onChange={handleChange}
					renderValue={(selected) => {
						const shouldSplit = props.attribute === 'yearRoot';
						const getDisplayLabel = (label) =>
							label?.includes('/') && shouldSplit ? label.split('/')[1] : label;

						return multiple ? (
							<OverflowChips
								items={selected}
								limitTags={limitTags}
								className='refinement-list-item'
								getLabel={(value) => getDisplayLabel(value.label)}
							/>
						) : (
							getDisplayLabel(tabFilteredItems.find((i) => i.isRefined)?.label)
						);
					}}
				>
					{allItems.map((item) => {
						return (
							<MenuItem key={`${title}-select-${item.label}`} value={item.value}>
								{multiple &&
									(item.isRefined ? <CheckBox /> : <CheckBoxOutlineBlank />)}
								<ListItemText
									primary={
										item.label?.includes('/') && shouldSplit
											? item.label.split('/')[1]
											: item.label
									}
								/>
							</MenuItem>
						);
					})}
				</Select>
			</Tooltip>
		</Stack>
	);
};

RefinementListSelect.displayName = 'RefinementListSelect';
